import { useState, useEffect, useCallback } from 'react';
import { readScoresData, readResponses, readUserInfo, writeSheetData } from '../../shared/utils/googleSheetsApi';

export const useFetchEntries = (API_BASE) => {
  const [scoreData, setScoreData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await readScoresData(API_BASE);
      setScoreData(data);
    } catch (e) {
      if (e.status === 401) {
        setSessionExpired(true);
      } else {
        setError('Failed to load data. Please try again later.');
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Runs on mount, same behaviour as your useEffect
  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  return { scoreData, loading, error, sessionExpired, setSessionExpired, loadEntries };
};


//Load Current User
export const useUser = (API_BASE, setSessionExpired) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    async function fetchUser() {
      try {
        const data = await readUserInfo(API_BASE);
        setUser(data);
        if (data) localStorage.setItem('ossdf_hasLoggedIn', 'true');
      } catch (e) {
        if (e.status === 401) setSessionExpired(true);
        else console.error('Failed to fetch user info', e);
      }
    }
    fetchUser();
  }, [API_BASE]);

  return { user };
};

// Fetches all response rows for the currently logged-in user.
// Re-fetches whenever `user` changes (i.e. on login).

export const useJudgeResponses = (API_BASE, user, setSessionExpired) => {
  const [judgeResponses, setJudgeResponses] = useState([]);

  const loadResponses = useCallback(async () => {
    if (!user?.email) {
      setJudgeResponses([]);
      return;
    }
    try {
      const respData = await readResponses(API_BASE, "Responses");
      if (!Array.isArray(respData) || respData.length === 0) {
        setJudgeResponses([]);
        return;
      }
      const header = respData[0] || [];
      const body = respData.slice(1).filter(row => {
        if (!row || row.length === 0) return false;
        return String(row[0] || '').toLowerCase().trim() === user.email.toLowerCase().trim();
      });
      setJudgeResponses([header, ...body]);
    } catch (e) {
      if (e.status === 401) setSessionExpired(true);
      else console.error('Failed to fetch responses for user', e);
      setJudgeResponses([]);
    }
  }, [API_BASE, user]);

  useEffect(() => {
    loadResponses();
  }, [loadResponses]);

  return { judgeResponses, setJudgeResponses, loadResponses };
};



export const useJudgesChoice = (API_BASE, user, judgesColIndexes, setSessionExpired) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const { entryIdx, artisticIdx, technicalIdx, nomIdx, favIdx } = judgesColIndexes;

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    try {
      const respData = await readResponses(API_BASE, "Responses");
      if (!Array.isArray(respData) || respData.length === 0) {
        setData([]); return;
      }
      const userEmail = user?.email || "";
      const userNominations = respData.slice(1).filter(
        row => row &&
          userEmail &&
          row[0] === userEmail &&
          row[nomIdx] &&
          row[nomIdx].toLowerCase() === 'true'
      );
      setData(userNominations);
    } catch (e) {
      if (e.status === 401) setSessionExpired(true);
      else console.error('Failed to fetch judges choice nominations', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, user, nomIdx, setSessionExpired]);


  const handleClose = useCallback(async (updatedRows = []) => {
    setOpen(false);
    console.log("I'm closing!", updatedRows);

    // Diff: only write rows where the favourite value actually changed
    const originalByEntry = new Map(
      (data || []).map(r => [String(r[entryIdx]), r])
    );

    const changedRows = (updatedRows || [])
      .filter(row => {
        const original = originalByEntry.get(String(row[entryIdx]));
        if (!original) return false;
        const oldNom = String(original[nomIdx] || "").toLowerCase() === "true";
        const newNom = String(row[nomIdx] || "").toLowerCase() === "true";
        const oldFav = String(original[favIdx] || "").toLowerCase() === "true";
        const newFav = String(row[favIdx] || "").toLowerCase() === "true";
        return oldNom !== newNom || oldFav !== newFav;
      })
      .map(row => {
        const next = [...row];
        next[entryIdx] = Number(next[entryIdx]) || next[entryIdx];
        next[artisticIdx] = Number(next[artisticIdx]) || next[artisticIdx];
        next[technicalIdx] = Number(next[technicalIdx]) || next[technicalIdx];
        return next;
      });

    if (changedRows.length === 0) return;

    try {
      await Promise.all(
        changedRows.map(row => writeSheetData({ row, sheet: "Responses", API_BASE }))
      );
      setData(updatedRows); // keep local state in sync for if dialog is reopened
    } catch (e) {
      console.error("Failed to save favourite updates", e);
      alert("Failed to save favourite updates. Please try again.");
    }
  }, [API_BASE, data, entryIdx, favIdx, artisticIdx, technicalIdx]);
  useJudgeResponses(API_BASE, user, setSessionExpired); // Ensure judge responses are fresh for the current user

  return { open, loading, data, handleOpen, handleClose };
};


// Owns state and loading logic for the read-only Submitted Awards dialog.

export const useSpecialAwards = (API_BASE, setSessionExpired) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [schools, setSchools] = useState([]);

  const handleOpen = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    try {
      const [respData, schoolsData] = await Promise.all([
        readResponses(API_BASE, "Specials"),
        readResponses(API_BASE, "Schools"),
      ])

      if (Array.isArray(respData) && respData.length > 0) {
        const submitted = respData.slice(1).filter(
          row => row && String(row[6] || '').trim().toLowerCase() === 'submitted'
        );
        setData(submitted);
      } else {
        setData([]);
      }

      // extract column C (index 2), skip header row, drop blanks
      const allSchools = (schoolsData || []).slice(1)
        .map(row => row[2])
        .filter(Boolean);
      setSchools(allSchools);


    } catch (e) {
      if (e.status === 401) setSessionExpired(true);
      else console.error('Failed to fetch special awards', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, setSessionExpired]);

  const handleClose = useCallback(() => setOpen(false), []);

  return { open, loading, data, schools, handleOpen, handleClose };
};